import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { action } from '@ember/object'
import { service } from '@ember/service'
import type BookLibraryCollectionService from 'teacher-ui-client/services/book-library/collection'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import { fn } from '@ember/helper'
import { t } from 'ember-intl'
import type BookLibraryBook from 'teacher-ui-client/models/book-library/book'
import type BookLibraryCollection from 'teacher-ui-client/models/book-library/collection'
import { Tooltip } from 'teacher-ui-client/components/tooltip'
import { BookLibraryBookCardHeader } from 'teacher-ui-client/components/book-library/book-card/header'
import { BookLibraryBookCardBody } from 'teacher-ui-client/components/book-library/book-card/body'
import { BookLibraryBookCardFooter } from 'teacher-ui-client/components/book-library/book-card/footer'

interface HandleCollectionActionResponse {
  ok: boolean
  errors?: unknown[]
  count: number
  title: string
}

interface Signature {
  Element: HTMLDivElement
  Args: {
    fadeIn?: boolean
    book: BookLibraryBook
    collections: BookLibraryCollection[]
    linkToBookDetail?: boolean
    showResourcesPill: boolean
  }
}

export class BookLibraryBookCard extends Component<Signature> {
  @service('book-library/collection') collection!: BookLibraryCollectionService

  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service log!: Log

  @tracked coverLoaded = false

  @action
  onCoverLoad() {
    this.coverLoaded = true
  }

  handleCollectionAction(
    actionName: string,
    collectionName: string,
    { ok, errors, count, title }: HandleCollectionActionResponse,
  ) {
    const { intl, flashQueue, log } = this
    if (ok) {
      flashQueue.addSuccess({
        subtitle: intl.t(`bookLibrary.collections.actions.${actionName}.success`, {
          collectionName,
          count,
          title,
          htmlSafe: true,
        }),
      })
    } else {
      flashQueue.addFail({
        subtitle: intl.t(`bookLibrary.collections.actions.${actionName}.fail`, { count, title, collectionName }),
      })
      log.error('book library collection action failed', errors)
    }
  }

  @action
  async addToCollection(book: BookLibraryBook, collection?: BookLibraryCollection, wasCreated?: boolean) {
    if (!collection) {
      return
    }
    const { ok, errors } = await this.collection.addBooks.perform(collection, [book])
    const actionName = wasCreated ? 'addToNew' : 'addTo'
    this.handleCollectionAction(actionName, collection.name, { ok, errors, count: 1, title: book.title })
  }

  @action
  async removeFromCollection(book: BookLibraryBook, collection?: BookLibraryCollection) {
    if (!collection) {
      return
    }
    const { ok, errors } = await this.collection.removeBooks.perform(collection, [book])
    this.handleCollectionAction('removeFrom', collection?.name, { ok, errors, count: 1, title: book.title })
  }

  @action
  onCreateCollection(book: BookLibraryBook, collection: BookLibraryCollection, errors?: unknown[]) {
    if (errors) {
      this.handleCollectionAction('create', collection?.name, { ok: false, errors, count: 1, title: book.title })
    } else {
      void this.addToCollection(book, collection, true)
    }
  }

  <template>
    {{#if @book.id}}
      <div
        class="inline-block {{if @fadeIn 'transition-opacity duration-500'}} {{unless this.coverLoaded 'opacity-0'}}"
        ...attributes
      >
        {{#unless @book.exclude}}
          <BookLibraryBookCardHeader
            class="flex justify-end"
            @bookId={{@book.id}}
            @bookTitle={{@book.title}}
            @collections={{@collections}}
            @onCreateCollection={{fn this.onCreateCollection @book}}
            @addToCollectionAction={{fn this.addToCollection @book}}
            @removeFromCollectionAction={{fn this.removeFromCollection @book}}
          />
        {{/unless}}
        {{#if @book.exclude}}
          <Tooltip @text={{t "bookLibrary.components.card.isExcluded"}} />
        {{/if}}
        <BookLibraryBookCardBody
          class="{{if @book.exclude 'opacity-30'}}"
          @bookPreviewCover={{@book.bookPreviewImg}}
          @bookFullCover={{@book.bookFullImg}}
          @bookCoverAlt={{@book.title}}
          @onCoverLoad={{this.onCoverLoad}}
          @linkToBookDetail={{@linkToBookDetail}}
          @bookId={{@book.id}}
        />
        <BookLibraryBookCardFooter
          class="text-right text-sm {{if @book.exclude 'opacity-50'}}"
          @bookId={{@book.id}}
          @showResourcesPill={{@showResourcesPill}}
        />
      </div>
    {{/if}}
  </template>
}

export default BookLibraryBookCard
